import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { parseGtmValue } from "../utils/gtm"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/Table"

export default function AnalyticsDetails() {
  const user = useUser()
  const { websiteId, gaId } = useParams()
  const [ga4, setGa4] = useState([])
  const [legacy, setLegacy] = useState([])
  const [url, setUrl] = useState("")
  const [gaContents, setGaContents] = useState("")
  const [loadingContents, setLoadingContents] = useState(false)
  const [parsedContents, setParsedContents] = useState(null)
  const [organizationId, setOrganizationId] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()

  const fetchDetails = useCallback(async () => {
    if (!user || !websiteId) return

    let ga4Query = supabase
      .from("ga4_instances")
      .select("id, ga_id, url")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })
    if (gaId) ga4Query = ga4Query.eq("ga_id", gaId)

    const legacyQuery = supabase
      .from("legacy_code_instances")
      .select("id, code, url")
      .eq("user_id", user.id)
      .eq("website_id", websiteId)
      .order("found_date", { ascending: false })

    const [{ data: ga4Data }, { data: legacyData }] = await Promise.all([
      ga4Query,
      legacyQuery,
    ])

    if (ga4Data?.length) {
      setGa4(ga4Data)
      setUrl(ga4Data[0].url)
    }
    if (legacyData?.length) {
      setLegacy(legacyData)
      setUrl((currentUrl) => currentUrl || legacyData[0].url)
    }
  }, [gaId, user, websiteId])

  const fetchOrganization = useCallback(async () => {
    if (!websiteId) return

    const { data } = await supabase
      .from("website_urls")
      .select("organization_id")
      .eq("id", websiteId)
      .single()
    if (data) setOrganizationId(data.organization_id)
  }, [websiteId])

  useEffect(() => {
    fetchDetails()
    fetchOrganization()
  }, [fetchDetails, fetchOrganization])

  const extractDataSection = (script) => {
    const start = script.indexOf("var data =")
    if (start === -1) return ""
    let idx = script.indexOf("{", start)
    if (idx === -1) return ""
    let depth = 1
    idx += 1
    while (idx < script.length && depth > 0) {
      const ch = script[idx]
      if (ch === "{") depth += 1
      else if (ch === "}") depth -= 1
      idx += 1
    }
    if (script[idx] === ";") idx += 1
    return script.slice(start, idx)
  }

  const saveContainerContents = async (snippet) => {
    const data = {
      user_id: user.id,
      website_id: websiteId,
      organization_id: organizationId,
      ga_id: gaId,
      contents: snippet,
    }

    await supabase
      .from("ga4_container_contents")
      .upsert(data, { onConflict: ["user_id", "website_id", "ga_id"] })
  }

  const normalizeCollection = (items) => {
    if (!Array.isArray(items)) return []
    return items.map((item) => {
      const normalized = parseGtmValue(item)
      if (normalized && typeof normalized === "object" && !Array.isArray(normalized)) {
        return normalized
      }
      return { value: normalized }
    })
  }

  const parseGaData = (snippet) => {
    if (!snippet) return null
    try {
      const json = snippet.replace(/^var data\s*=\s*/, "").replace(/;\s*$/, "")
      const obj = JSON.parse(json)
      const resource = obj.resource || {}
      return {
        tags: normalizeCollection(resource.tags || resource.tag),
        macros: normalizeCollection(resource.macros || resource.macro),
        triggers: normalizeCollection(resource.predicates || resource.predicate),
        rules: normalizeCollection(resource.rules || resource.rule),
      }
    } catch (error) {
      console.warn("Failed to parse GA4 inspect data", error)
      return null
    }
  }

  const summarizeCrossDomainPatterns = (rawValue) => {
    if (!Array.isArray(rawValue)) return null
    const values = rawValue[0] === "list" ? rawValue.slice(1) : rawValue
    if (!values.length || values.some((item) => typeof item !== "string")) {
      return null
    }

    const categories = {
      matchesRegex: [],
      contains: [],
      beginsWith: [],
      endsWith: [],
      exactlyMatches: [],
    }

    values.forEach((pattern) => {
      let working = pattern
      let bucket = "matchesRegex"

      if (working.startsWith("^") && working.endsWith("$")) {
        working = working.slice(1, -1)
        bucket = "exactlyMatches"
      } else if (working.startsWith("^")) {
        working = working.slice(1)
        bucket = "beginsWith"
      } else if (working.endsWith("$")) {
        working = working.slice(0, -1)
        bucket = "endsWith"
      } else if (working.includes("\\.")) {
        bucket = "contains"
      } else if (working.includes(".")) {
        bucket = "contains"
      }

      const cleaned = working
        .replace(/\\\\/g, "\\")
        .replace(/\\\./g, ".")

      categories[bucket].push(cleaned)
    })

    const ordered = [
      { label: "Matches Regex", key: "matchesRegex" },
      { label: "Contains", key: "contains" },
      { label: "Begins With", key: "beginsWith" },
      { label: "Ends With", key: "endsWith" },
      { label: "Exactly Matches", key: "exactlyMatches" },
    ]

    const lines = ordered
      .filter(({ key }) => categories[key].length)
      .map(({ label, key }) => ({ label, values: categories[key] }))

    return lines.length ? lines : null
  }

  const extractEventNameFromNode = (node) => {
    if (!node) return null

    const tryArray = (arr) => {
      for (const item of arr) {
        const found = extractEventNameFromNode(item)
        if (found) return found
      }
      return null
    }

    if (typeof node === "string") {
      const sanitized = node.replace(/\\"/g, '"')
      try {
        const parsed = JSON.parse(sanitized)
        const parsedResult = extractEventNameFromNode(parsed)
        if (parsedResult) return parsedResult
      } catch {
        // Ignore JSON parse errors and fall through to regex extraction
      }

      const match = sanitized.match(/"stringValue":"([^"\\]+)"/)
      if (match) return match[1]
      return null
    }

    if (Array.isArray(node)) {
      return tryArray(node)
    }

    if (typeof node === "object") {
      const values = Object.values(node)
      const fromValues = tryArray(values)
      if (fromValues) return fromValues

      const keys = Object.keys(node)
      return tryArray(keys)
    }

    return null
  }

  const collectConversionRuleNames = (rawValue) => {
    if (!Array.isArray(rawValue)) return null
    const entries = rawValue[0] === "list" ? rawValue.slice(1) : rawValue

    const events = entries
      .map((entry) => {
        if (!Array.isArray(entry)) return null
        const details = entry.length > 2 ? entry[2] : entry[entry.length - 1]
        return extractEventNameFromNode(details)
      })
      .filter(Boolean)

    const uniqueEvents = Array.from(new Set(events))
    return uniqueEvents.length ? uniqueEvents : null
  }

  const collectCrossDomainRows = (rows) => {
    if (!Array.isArray(rows)) return []

    const seen = new Set()
    const results = []

    rows.forEach((row) => {
      if (!row || row.function !== "__ogt_cross_domain") return

      const segments = Object.entries(row)
        .filter(([key]) => key !== "function")
        .flatMap(([, value]) => summarizeCrossDomainPatterns(value) || [])

      segments.forEach(({ label, values }) => {
        values.forEach((value) => {
          const trimmed = value.trim()
          if (!trimmed) return
          const key = `${label}|${trimmed}`
          if (seen.has(key)) return
          seen.add(key)
          results.push({
            patternType: label,
            value: trimmed,
          })
        })
      })
    })

    const categoryOrder = [
      "Matches Regex",
      "Contains",
      "Begins With",
      "Ends With",
      "Exactly Matches",
    ]

    return results.sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a.patternType)
      const bIndex = categoryOrder.indexOf(b.patternType)
      const categoryDiff = (aIndex === -1 ? categoryOrder.length : aIndex) -
        (bIndex === -1 ? categoryOrder.length : bIndex)
      if (categoryDiff !== 0) return categoryDiff
      return a.value.localeCompare(b.value)
    })
  }

  const collectConversionEventRows = (rows) => {
    if (!Array.isArray(rows)) return []

    const seen = new Set()
    const events = []

    rows.forEach((row) => {
      if (!row || row.function !== "__ccd_conversion_marking") return

      const names = collectConversionRuleNames(row.vtp_conversionRules)
      names?.forEach((name) => {
        const trimmed = name.trim()
        if (!trimmed || seen.has(trimmed)) return
        seen.add(trimmed)
        events.push({ eventName: trimmed })
      })
    })

    return events
  }

  const formatCellValue = (value, key, row) => {
    if (value === null || value === undefined) return ""
    if (Array.isArray(value) || (value && typeof value === "object")) {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  const renderTable = (rows) => {
    if (!rows || rows.length === 0) {
      return <p>No data found.</p>
    }

    const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))

    return (
      <TableContainer className="ui-table-container--scroll" style={{ marginTop: "0.5rem" }}>
        <Table>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableHeaderCell key={key}>{key}</TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {keys.map((key) => {
                  const formatted = formatCellValue(row[key], key, row)
                  const isMultiline =
                    typeof formatted === "string" && formatted.includes("\n")

                  return (
                    <TableCell key={key}>
                      {isMultiline ? (
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                          }}
                        >
                          {formatted}
                        </pre>
                      ) : (
                        <span
                          style={{
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                          }}
                        >
                          {formatted}
                        </span>
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const renderCustomTable = (rows, columns, emptyMessage = "No data found.") => {
    if (!rows || rows.length === 0) {
      return <p>{emptyMessage}</p>
    }

    return (
      <TableContainer className="ui-table-container--scroll" style={{ marginTop: "0.5rem" }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableHeaderCell key={column.key}>{column.label}</TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column) => (
                  <TableCell key={column.key}>{row[column.key]}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const inspectContents = async () => {
    if (!gaId) return
    setLoadingContents(true)
    try {
      const res = await fetch(
        `https://www.googletagmanager.com/gtag/js?id=${gaId}`
      )
      const text = await res.text()
      const snippet = extractDataSection(text)
      const parsed = parseGaData(snippet)
      setGaContents(snippet)
      setParsedContents(parsed)
      await saveContainerContents(snippet)
    } catch {
      setGaContents("Failed to fetch contents")
      setParsedContents(null)
    }
    setLoadingContents(false)
  }

  const macroRows = parsedContents?.macros ?? []
  const tagRows = parsedContents?.tags ?? []
  const crossDomainRows = parsedContents
    ? collectCrossDomainRows([...macroRows, ...tagRows])
    : []
  const conversionEventRows = parsedContents
    ? collectConversionEventRows(tagRows)
    : []

  return (
    <div className="page-container">
      <h2>Analytics Details for {url}</h2>
      <button
        onClick={() => {
          const fromPath = location.state?.from
          if (fromPath) navigate(fromPath)
          else navigate("/website-audit/scraping")
        }}
      >
        Back
      </button>
      <h3 style={{ marginTop: "1rem" }}>GA4 IDs</h3>
      <ul>
        {ga4.map((row) => (
          <li key={row.id}>{row.ga_id}</li>
        ))}
      </ul>
      {gaId && (
        <div style={{ marginTop: "1rem" }}>
          <button onClick={inspectContents} disabled={loadingContents}>
            {loadingContents ? "Loading..." : "Inspect GA4 Contents"}
          </button>
          {gaContents && (
            <pre
              style={{
                marginTop: "0.5rem",
                background: "#f5f5f5",
                color: "#111",
                padding: "1rem",
                maxHeight: "400px",
                overflow: "auto",
              }}
            >
              {gaContents}
            </pre>
          )}
          {parsedContents && (
            <div style={{ marginTop: "1rem" }}>
              {(conversionEventRows.length > 0 || crossDomainRows.length > 0) && (
                <section style={{ marginTop: "0.5rem" }}>
                  <h4>Cleaned GA4 Data</h4>
                  {conversionEventRows.length > 0 && (
                    <section style={{ marginTop: "0.75rem" }}>
                      <h5 style={{ marginBottom: "0.25rem" }}>Conversion Events</h5>
                      {renderCustomTable(conversionEventRows, [
                        { key: "eventName", label: "Event Name" },
                      ])}
                    </section>
                  )}
                  {crossDomainRows.length > 0 && (
                    <section style={{ marginTop: "0.75rem" }}>
                      <h5 style={{ marginBottom: "0.25rem" }}>Cross Domain Tracking</h5>
                      {renderCustomTable(
                        crossDomainRows,
                        [
                          { key: "patternType", label: "Pattern Type" },
                          { key: "value", label: "Value" },
                        ],
                        "No cross domain tracking configured."
                      )}
                    </section>
                  )}
                </section>
              )}
              <section style={{ marginTop: "1rem" }}>
                <h4>Parsed GA4 Configuration</h4>
                <section style={{ marginTop: "0.5rem" }}>
                  <h5 style={{ marginBottom: "0.25rem" }}>Tags</h5>
                  {renderTable(parsedContents.tags)}
                </section>
                <section style={{ marginTop: "1rem" }}>
                  <h5 style={{ marginBottom: "0.25rem" }}>Macros</h5>
                  {renderTable(parsedContents.macros)}
                </section>
                <section style={{ marginTop: "1rem" }}>
                  <h5 style={{ marginBottom: "0.25rem" }}>Triggers</h5>
                  {renderTable(parsedContents.triggers)}
                </section>
                <section style={{ marginTop: "1rem" }}>
                  <h5 style={{ marginBottom: "0.25rem" }}>Rules</h5>
                  {renderTable(parsedContents.rules)}
                </section>
              </section>
            </div>
          )}
        </div>
      )}
      <h3>Legacy Codes</h3>
      <ul>
        {legacy.map((row) => (
          <li key={row.id}>{row.code}</li>
        ))}
      </ul>
    </div>
  )
}
